import { countryCodes, type CountryCode } from '@inkitt/geo';
import { z } from 'zod';
import { c } from '../../contract';
import { PointsBundle, dynamicPaywallPriceData } from '../../types';
import { ProratedStripeUpgradeData } from '../../types/ProratedStripeUpgradePrice';

export type MobileAttribution = {
  adid?: string;
  idfa?: string;
  idfv?: string;
  osName?: string;
  isInAppPayment?: boolean;
  seriesId?: string;
  episodeId?: string;
  testBranch?: string;
};

export const paywallRouter = c.router(
  {
    createMobileHostedCheckout: {
      summary: 'Creates a hosted Stripe checkout for a Galatea mobile purchase',
      method: 'POST',
      path: '/stripe/mobile-hosted-checkout',
      headers: z.object({
        applicationbundleid: z.string().optional(),
        visitorid: z.string().optional(),
        visitid: z.string().optional(),
      }),
      body: z
        .object({
          productId: z.string().min(1).max(500).optional(),
          sku: z.string().min(1).max(500).optional(),
          promoCode: z.string().max(500).optional(),
          purchaseCode: z.string().max(500).optional(),
          storeCountryCode: z.enum(countryCodes).optional(),
          currencyCode: z
            .string()
            .regex(/^[A-Z]{3}$/)
            .optional(),
          language: z
            .string()
            .max(35)
            .regex(/^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/),
          os: z.enum(['ios', 'android']),
          externalTransactionToken: z.string().max(500).optional(),
          forceSubscriptionDelay: z.boolean().optional(),
          isWebRedesignEligible: z.boolean().optional(),
          originalPriceString: z.string().max(20).optional(),
          appAttribution: z
            .object({
              adid: z.string().max(500).optional(),
              idfa: z.string().max(500).optional(),
              idfv: z.string().max(500).optional(),
            })
            .strict()
            .optional(),
          storyAttribution: z
            .object({
              seriesId: z.string().max(500).optional(),
              episodeId: z.string().max(500).optional(),
              testBranch: z.string().max(500).optional(),
              metadataTestVariantId: z.string().max(500).optional(),
              metadataTestVariantName: z.string().max(500).optional(),
            })
            .strict()
            .optional(),
        })
        .strict()
        .refine((body) => Boolean(body.productId || body.sku), 'A product ID or SKU is required'),
      responses: {
        200: z.object({ url: z.string().url() }),
        204: z.undefined(),
        403: z.literal('Forbidden'),
        404: z.literal('Not found'),
        409: z.object({ reason: z.literal('already_subscribed'), url: z.string().url() }),
        422: z.literal('unprocessable'),
        500: z.literal('Internal Server Error'),
      },
    },
    validateForWeb: {
      summary: 'Validates if the product can be purchased on stripe web for the given productId, price and currency',
      method: 'POST',
      path: '/validate-product-for-stripe',
      body: z.object({
        productId: z.string(),
        price: z.number(),
        currency: z.string(),
        storeCountryCode: z.string(),
        promoCode: z.string().optional(),
      }),
      responses: {
        200: z.object({
          isValid: z.boolean(),
        }),
      },
    },
    getPointsMap: {
      summary: 'Gets a mapping of package ID to points',
      method: 'GET',
      path: '/map',
      responses: {
        200: z.record(z.string(), z.array(PointsBundle)),
      },
    },
    getProratedStripeUpgradePrice: {
      summary: 'Gets the prorated price for upgrading a subscription',
      method: 'GET',
      path: '/stripe/prorated-upgrade',
      query: z.object({
        upgradeProductSku: z.string(),
        subscriptionIdToUpgrade: z.string(),
        storeCountryCode: z.string().length(2),
        /** A template used to format the price strings. Must have `{price}` as the replacement section. */
        localePriceString: z.string().refine((val) => val.includes('{price}'), {
          message: "Must include '{price}' as the replacement section",
        }),
        /** If provided, we will use this to create the price equivalent string. */
        baseSubscriptionPeriod: z.string().optional(),
      }),
      responses: {
        200: ProratedStripeUpgradeData.or(z.null()),
      },
    },
    getDynamicPaywallOfferPrice: {
      summary: 'Gets the price for a dynamic paywall offer',
      method: 'GET',
      path: '/stripe/dynamic-paywall-offer-price',
      query: z.object({
        sku: z.string(),
        promocode: z.string().optional(),
        storeCountryCode: z.string().length(2),
        /** A template used to format the price strings. Must have `{price}` as the replacement section. */
        localePriceString: z.string().refine((val) => val.includes('{price}'), {
          message: "Must include '{price}' as the replacement section",
        }),
        /** If provided, we will use this to create the price equivalent string. */
        baseSubscriptionPeriod: z.string().optional(),
      }),
      responses: {
        200: z
          .object({
            purchaseUrl: z.string(),
            pricingData: dynamicPaywallPriceData,
          })
          .or(z.null()),
      },
    },
    getSkuFromRevCatProductId: {
      summary: 'Gets the sku for the stripe product matching a RevCat product ID',
      method: 'GET',
      path: '/sku/:productId',
      pathParams: z.object({
        productId: z.string(),
      }),
      responses: {
        200: z.string().nullish(),
      },
    },
    syncStripeCustomer: {
      summary: 'Syncs the Stripe customer email for the authenticated user',
      method: 'POST',
      path: '/stripe/sync-customer',
      body: c.type<Record<string, never>>(),
      responses: {
        200: c.type<{ success: true }>(),
        422: z.literal('unprocessable'),
      },
    },
    deferGoogleSubscription: {
      summary: 'Defers the given Google Play subscription for the authenticated user by the requested number of days',
      method: 'POST',
      path: '/defer-google-subscription',
      body: z.object({
        productIdentifier: z.string(),
        extendByDays: z.number().int().min(1).max(365),
      }),
      responses: {
        200: z.object({ success: z.literal(true) }),
        422: z.literal('unprocessable'),
      },
    },
    confirmApplePaySubscription: {
      summary: 'Creates a Stripe subscription using a payment method and billing details collected from Apple Pay',
      method: 'POST',
      path: '/stripe/apple-pay-subscription',
      query: c.type<{ countryCode: CountryCode }>(),
      body: c.type<{
        productId: string;
        paymentMethodId: string;
        billingCountry: string;
        billingPostalCode?: string;
        trialDays?: number;
        attribution?: MobileAttribution;
        applePayEmail?: string;
      }>(),
      responses: {
        200: c.type<{ success: true }>(),
        422: z.literal('unprocessable'),
      },
    },
  },
  { pathPrefix: '/paywall' },
);
